/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */
import React, { FC, useEffect, useMemo, useState } from "react";
import { Navbar } from "./components/UnAuthenticationLayout/Navbar";
import { Footer } from "./components/UnAuthenticationLayout/Footer";
import {
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PageNotFound } from "./components/PageNotFound";
import LoginContainer from "./pages/unauthenticated/LoginContainer";
import { Sidebar } from "./components/Sidebar";
import AccountContainer from "./pages/authenticated/account/AccountContainer";
import { useSelector } from "react-redux";
import { IRootState } from "./config/reducers";
import ExamContainer from "./pages/authenticated/exams/ExamsContainer";
import ExamDetailsContainer from "./pages/authenticated/exams/ExamDetailsContainer";
import { useMatomo } from "@datapunt/matomo-tracker-react";
import DashboardContainer from "./pages/unauthenticated/DashboardContainer";
import { Banner } from "./components/Dashboard/Banner";
import { useHeroLayout } from "./hooks/useHeroLayout";
import { TermsOfServicePage } from "@src/pages/authenticated/userLicense/TermsOfServicePage";
import { getEula } from "@src/pages/authenticated/userLicense/ducks/operators";
import { useAppDispatch } from "@src/config/store";
import { LoginAction } from "@src/reducers/slices/loginSlice";
import { useDetectSmallScreen } from "./hooks/useDetectSmallScreen";
import OauthCallback from "@src/pages/authenticated/account/OauthCallback";
import { AppAlert, NetworkRequestErrorAlert } from "@advapacs/common";
import { faro } from "@grafana/faro-web-sdk";
import { getRegion } from "@advapacs/advapacs-ui/src/utils/utils";

const NoWrapperContentLayout = (): JSX.Element => {
  const { pathname } = useLocation();
  const isHeroLayout = useHeroLayout();
  const showFooter = useMemo(() => {
    return !pathname.includes("/OAuthCallback");
  }, [pathname]);

  return (
    <>
      <Outlet />
      {isHeroLayout && showFooter && <Footer />}
    </>
  );
};

const WrapperLayout = (): JSX.Element => {
  return (
    <div className="content-wrapper">
      <div className="content container">
        <NetworkRequestErrorAlert />
        <Outlet />
      </div>
    </div>
  );
};

const UnauthenticatedRoutes: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHeroLayout = useHeroLayout();
  const isSmallScreen = useDetectSmallScreen();
  const { trackPageView } = useMatomo();
  const { isAuthenticated, sessionHasBeenFetched } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );

  useEffect(() => {
    trackPageView({
      customDimensions: [
        {
          id: 1,
          // @ts-ignore
          value: localStorage.getItem("accessId"),
        },
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    if (isAuthenticated && sessionHasBeenFetched) navigate("/Exams");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionHasBeenFetched]);

  const PageContentRoutes = ["/Exams", "/MyAccount", "/t"];

  const showNavbar = useMemo(() => {
    return !pathname.includes("/OAuthCallback");
  }, [pathname]);

  const showFooter = useMemo(() => {
    return !pathname.includes("/OAuthCallback") && !isHeroLayout;
  }, [pathname, isHeroLayout]);

  // FARO Checking router changes.
  useEffect(() => {
    // tracking router with faro.
    faro.api?.setView({ name: pathname });
  }, [pathname]);

  return (
    <>
      {showNavbar && <Navbar />}
      {pathname.includes("/Dashboard") && <Banner />}
      <div
        className={` ${
          !PageContentRoutes.includes(pathname)
            ? "page__banner"
            : "page-content"
        } ${isSmallScreen ? "small-screen" : ""}`}
      >
        <Routes>
          <Route
            path="/public/terms-of-service"
            element={<TermsOfServicePage />}
          />
          <Route element={<NoWrapperContentLayout />}>
            <Route path="/OAuthCallback" element={<OauthCallback />} />
            <Route index path="/t/:token" element={<LoginContainer />} />
          </Route>

          <Route element={<WrapperLayout />}>
            <Route index path="/Dashboard" element={<DashboardContainer />} />
            <Route index path="/" element={<Navigate to={"/Dashboard"} />} />
            <Route path="*" element={<PageNotFound />} />
          </Route>
        </Routes>
        {showFooter && <Footer />}
      </div>
    </>
  );
};

const AuthenticatedRoutes: FC = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [logoutErrorMessage, setLogoutErrorMessage] = useState(null);
  const { isMobileOnly } = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const { account, isAuthenticated, sessionHasBeenFetched } = useSelector(
    (state: IRootState) => state.unAuthenticatedReducer
  );
  const { eulaDetail } = useSelector(
    (state: IRootState) => state.userLicenseReducer
  );

  useEffect(() => {
    if (!eulaDetail) {
      dispatch(getEula());
      dispatch(LoginAction.setIsShowEulaAfterLogin(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eulaDetail]);

  useEffect(() => {
    const isAuth =
      isAuthenticated &&
      sessionHasBeenFetched &&
      !localStorage.getItem("newAccessId");
    if (isAuth && eulaDetail?.eula?.id) navigate("/terms-of-service");
    else if (isAuth) {
      //
      const shouldNotRedirect =
        window.location.pathname.includes("OAuthCallback") ||
        window.location.pathname.includes("MyAccount");
      if (!shouldNotRedirect) navigate("/Exams");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, sessionHasBeenFetched, eulaDetail?.eula]);

  const { trackPageView } = useMatomo();

  useEffect(() => {
    if (isAuthenticated && sessionHasBeenFetched) {
      trackPageView({
        customDimensions: [
          {
            id: 1,
            value: account?.customerId as string,
          },
          {
            id: 2,
            value: account?.id as string,
          },
        ],
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account, isAuthenticated, sessionHasBeenFetched, pathname]);

  // FARO Checking router changes.
  useEffect(() => {
    // tracking router with faro.
    faro.api?.setView({ name: pathname });
  }, [pathname]);

  // ============= FARO CURRENT CONTEXT =================
  useEffect(() => {
    if (isAuthenticated && account && sessionHasBeenFetched) {
      // tracking with user id and customer id.
      faro.api?.setUser({
        id: account.id,
        attributes: {
          customerId: account.customerId ?? "",
          region: getRegion(),
        },
      });
    } else {
      faro.api?.resetUser();
    }
  }, [isAuthenticated, account, sessionHasBeenFetched]);

  const showSidebar = useMemo(() => {
    return (
      !pathname.includes("/t/") &&
      !pathname.includes("/terms-of-service") &&
      !pathname.includes("/OAuthCallback")
    );
  }, [pathname]);

  const showNavbar = useMemo(() => {
    return !pathname.includes("/OAuthCallback");
  }, [pathname]);

  const showFooter = useMemo(() => {
    return !pathname.includes("/OAuthCallback");
  }, [pathname]);

  return (
    <>
      {showNavbar && (
        // @ts-ignore
        <Navbar setLogoutErrorMessage={setLogoutErrorMessage} isAuthenticated />
      )}
      <div className="page-content">
        {showSidebar && <Sidebar />}
        <div className="content-wrapper scrollable">
          {logoutErrorMessage && (
            <AppAlert.ErrorAlert
              dismissible
              className="m-t-xs"
              onClose={() => setLogoutErrorMessage(null)}
              error={logoutErrorMessage}
            />
          )}
          <div
            className={`content landing-content  ${
              isMobileOnly ? "mobile" : "desktop"
            }`}
          >
            <NetworkRequestErrorAlert />
            <Routes>
              <Route path="*" element={<PageNotFound />} />
              <Route
                path="/terms-of-service"
                element={<TermsOfServicePage />}
              />
              <Route path="/Exams" element={<ExamContainer />} />
              <Route index path="/t/:token" element={<LoginContainer />} />
              <Route
                path="/Exams/Studies/:studyId"
                element={<ExamDetailsContainer />}
              />
              <Route element={<NoWrapperContentLayout />}>
                <Route path="/OAuthCallback" element={<OauthCallback />} />
              </Route>

              <Route
                path="/Exams/Orders/:orderId"
                element={<ExamDetailsContainer />}
              />
              <Route path="/MyAccount" element={<AccountContainer />} />
              <Route path="/" element={<Navigate to={"/Exams"} />} />
            </Routes>
          </div>
        </div>
      </div>
      {showFooter && <Footer isAuthenticated />}
    </>
  );
};

export { UnauthenticatedRoutes, AuthenticatedRoutes };
